import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getWebappApiUrl, webappFetch } from '../utils/apiConfig';
import {
  INTEGRATION_FROM_BLOCK,
  IntegrationLedgerSnapshot,
  IntegrationLedgerTransaction,
  NetworkStats,
  classifyIntegrationTx,
  sortIntegrationTransactions
} from '../domain/integrationLedger';

interface UseIntegrationLedgerStreamOptions {
  fromBlock?: number;
  limit?: number;
  intervalMs?: number;
  fullRefreshMs?: number;
  enabled?: boolean;
}

export interface IntegrationLedgerStreamState {
  transactions: IntegrationLedgerTransaction[];
  businessRows: IntegrationLedgerTransaction[];
  v1Rows: IntegrationLedgerTransaction[];
  legacyRows: IntegrationLedgerTransaction[];
  lifecycleRows: IntegrationLedgerTransaction[];
  networkStats: NetworkStats | null;
  loading: boolean;
  error: string | null;
  lastSyncedAt: string | null;
  newTransactionIds: Set<string>;
  newBlockNumbers: Set<string>;
  reload: () => Promise<void>;
}

export function useIntegrationLedgerStream({
  fromBlock = INTEGRATION_FROM_BLOCK,
  limit = 500,
  intervalMs = 2000,
  fullRefreshMs = 30000,
  enabled = true
}: UseIntegrationLedgerStreamOptions = {}): IntegrationLedgerStreamState {
  const [transactions, setTransactions] = useState<IntegrationLedgerTransaction[]>([]);
  const [networkStats, setNetworkStats] = useState<NetworkStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastSyncedAt, setLastSyncedAt] = useState<string | null>(null);
  const [newTransactionIds, setNewTransactionIds] = useState<Set<string>>(new Set());
  const [newBlockNumbers, setNewBlockNumbers] = useState<Set<string>>(new Set());

  const loadingRef = useRef(false);
  const eventSourceRef = useRef<EventSource | null>(null);
  const knownTxIdsRef = useRef<Set<string>>(new Set());
  const knownBlockNumbersRef = useRef<Set<string>>(new Set());
  const initializedHighlightsRef = useRef(false);
  const highlightTimersRef = useRef<number[]>([]);

  const hydrateTransactions = useCallback((
    inputRows: IntegrationLedgerTransaction[],
    options: { animateNew?: boolean; syncedAt?: string } = {}
  ) => {
    const rows = sortIntegrationTransactions(inputRows);
    const nextTxIds = new Set(rows.map((tx) => tx.transaction_id).filter(Boolean) as string[]);
    const nextBlockNumbers = new Set(rows.map((tx) => tx.block_number).filter(Boolean) as string[]);

    if (initializedHighlightsRef.current && options.animateNew !== false) {
      const newTxIds = Array.from(nextTxIds).filter((id) => !knownTxIdsRef.current.has(id));
      const newBlocks = Array.from(nextBlockNumbers).filter((blockNumber) => !knownBlockNumbersRef.current.has(blockNumber));

      if (newTxIds.length || newBlocks.length) {
        setNewTransactionIds((previous) => {
          const merged = new Set(previous);
          newTxIds.forEach((id) => merged.add(id));
          return merged;
        });
        setNewBlockNumbers((previous) => {
          const merged = new Set(previous);
          newBlocks.forEach((blockNumber) => merged.add(blockNumber));
          return merged;
        });

        const timer = window.setTimeout(() => {
          setNewTransactionIds((previous) => {
            const next = new Set(previous);
            newTxIds.forEach((id) => next.delete(id));
            return next;
          });
          setNewBlockNumbers((previous) => {
            const next = new Set(previous);
            newBlocks.forEach((blockNumber) => next.delete(blockNumber));
            return next;
          });
        }, 4000);
        highlightTimersRef.current.push(timer);
      }
    }

    knownTxIdsRef.current = nextTxIds;
    knownBlockNumbersRef.current = nextBlockNumbers;
    initializedHighlightsRef.current = true;
    setTransactions(rows);
    if (options.syncedAt) {
      setLastSyncedAt(options.syncedAt);
    }
  }, []);

  const loadData = useCallback(async ({ initial = false }: { initial?: boolean } = {}) => {
    if (!enabled) {
      setLoading(false);
      return;
    }
    if (loadingRef.current) return;

    try {
      loadingRef.current = true;
      if (initial) setLoading(true);
      setError(null);

      const statsRequest = webappFetch(`${getWebappApiUrl()}/api/blockchain/statistics`)
        .then((response) => response.json())
        .catch(() => null);

      const response = await webappFetch(
        `${getWebappApiUrl()}/api/blockchain/integration-prototype/transactions?fromBlock=${fromBlock}&limit=${limit}`
      );
      const body = await response.json();

      if (!response.ok || !body.success) {
        throw new Error(body.message || body.error || `HTTP ${response.status}`);
      }

      const statsBody = await statsRequest;
      if (statsBody?.success && statsBody.data) {
        setNetworkStats({
          latestBlockNumber: Number(statsBody.data.latestBlockNumber || 0),
          totalTransactions: Number(statsBody.data.totalTransactions || 0)
        });
      }

      hydrateTransactions(body.data.transactions || [], {
        animateNew: !initial,
        syncedAt: body.data.emittedAt || new Date().toISOString()
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      if (initial) setLoading(false);
      loadingRef.current = false;
    }
  }, [enabled, fromBlock, hydrateTransactions, limit]);

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return;
    }
    loadData({ initial: true });
  }, [enabled, loadData]);

  useEffect(() => () => {
    highlightTimersRef.current.forEach((timer) => window.clearTimeout(timer));
    highlightTimersRef.current = [];
  }, []);

  useEffect(() => {
    if (!enabled) return undefined;
    setError(null);
    let closed = false;

    const source = new EventSource(
      `${getWebappApiUrl()}/api/blockchain/integration-prototype/events?fromBlock=${fromBlock}&limit=${limit}&intervalMs=${intervalMs}`,
      { withCredentials: true }
    );
    eventSourceRef.current = source;

    const readSnapshot = (event: Event): IntegrationLedgerSnapshot | null => {
      try {
        return JSON.parse((event as MessageEvent<string>).data) as IntegrationLedgerSnapshot;
      } catch {
        return null;
      }
    };

    const handleSnapshot = (event: Event) => {
      const body = readSnapshot(event);
      if (!body || closed) return;
      if (body.transactions) {
        hydrateTransactions(body.transactions, {
          syncedAt: body.emittedAt || new Date().toISOString()
        });
      }
    };

    const handleConnected = (event: Event) => {
      const body = readSnapshot(event);
      if (!body || closed) return;
      setLastSyncedAt(body.emittedAt || new Date().toISOString());
    };

    const handleHeartbeat = (event: Event) => {
      const body = readSnapshot(event);
      if (!body || closed) return;
      setLastSyncedAt(body.emittedAt || new Date().toISOString());
    };

    const handleServerError = (event: Event) => {
      const body = readSnapshot(event);
      if (body?.message) {
        setError(body.message);
      }
    };

    source.addEventListener('connected', handleConnected);
    source.addEventListener('snapshot', handleSnapshot);
    source.addEventListener('update', handleSnapshot);
    source.addEventListener('heartbeat', handleHeartbeat);
    source.addEventListener('server-error', handleServerError);
    source.onerror = () => undefined;

    const fullRefreshTimer = window.setInterval(() => loadData(), fullRefreshMs);

    return () => {
      closed = true;
      window.clearInterval(fullRefreshTimer);
      source.close();
      if (eventSourceRef.current === source) {
        eventSourceRef.current = null;
      }
    };
  }, [enabled, fromBlock, fullRefreshMs, hydrateTransactions, intervalMs, limit, loadData]);

  const categorized = useMemo(() => {
    const lifecycleRows = transactions.filter((tx) => classifyIntegrationTx(tx) === 'lifecycle');
    const businessRows = transactions.filter((tx) => classifyIntegrationTx(tx) !== 'lifecycle');
    const v1Rows = businessRows.filter((tx) => tx.function_name === 'RecordIntegrationResult');
    const legacyRows = businessRows.filter((tx) => tx.function_name === 'RecordEvent');
    return { lifecycleRows, businessRows, v1Rows, legacyRows };
  }, [transactions]);

  return {
    transactions,
    ...categorized,
    networkStats,
    loading,
    error,
    lastSyncedAt,
    newTransactionIds,
    newBlockNumbers,
    reload: loadData
  };
}
